// Active Builds, presentational: a rail of builds (one per client build) on the
// left, the selected build on the right — progress, release cadence, the queue
// of pages still to build (drag to reorder) and what has shipped. All data and
// writes come in through props from pages/ActiveBuilds.tsx.

import { useMemo, useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faCheck, faPen, faRotateLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Badge, Button, Icon, IconButton, Input, KebabMenu, SearchableSelect, SegmentedControl, Switch } from '../ui';
import { formatDate } from '../../utils/dateUtils';
import { cadenceLabel } from '../../lib/activeBuilds';
import type { ActiveBuild, BuildPage, BuildStatus, PageKind } from '../../lib/activeBuilds';

export interface BuildSummary {
  build: ActiveBuild;
  done: number;
  queued: number;
  backlog: number;
}

export interface BuildsViewProps {
  summaries: BuildSummary[];
  filter: BuildStatus;
  onFilter: (s: BuildStatus) => void;
  selected: ActiveBuild | null;
  onSelect: (id: string) => void;
  pages: BuildPage[];
  pagesLoading: boolean;
  assigneeName: string | null;
  nextRelease: string | null;
  busy: boolean;
  notice: string | null;
  error: string | null;
  onEdit: () => void;
  onToggleAuto: (next: boolean) => void;
  onReleaseNext: () => void;
  onAddPage: (title: string, path: string | null, kind: PageKind) => void;
  onSetStatus: (p: BuildPage, status: BuildPage['status']) => void;
  onReorderBacklog: (ids: string[]) => void;
  onSavePage: (p: BuildPage, patch: { title: string; path: string | null; note: string | null }) => void;
  onDeletePage: (p: BuildPage) => void;
}

const FILTERS: Array<{ id: BuildStatus; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'paused', label: 'Paused' },
  { id: 'finished', label: 'Finished' },
];

const eyebrow: React.CSSProperties = {
  fontSize: 'var(--text-2xs)',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-faint)',
  fontFamily: 'var(--font-mono)',
};

const surface: React.CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-rest)',
};

/** Done / on the board / backlog as one bar. */
function ProgressBar({ done, queued, total, height = 6 }: { done: number; queued: number; total: number; height?: number }) {
  const w = (n: number) => (total ? `${(n / total) * 100}%` : '0%');
  return (
    <div style={{ display: 'flex', height, borderRadius: 999, background: 'var(--surface-sunken, var(--surface-raised))', overflow: 'hidden' }}>
      <div style={{ width: w(done), background: 'var(--accent)', transition: 'width var(--dur-base) var(--ease-out)' }} />
      <div style={{ width: w(queued), background: 'var(--status-warn)', opacity: 0.85 }} />
    </div>
  );
}

function RailRow({ s, active, onClick }: { s: BuildSummary; active: boolean; onClick: () => void }) {
  const total = s.done + s.queued + s.backlog;
  return (
    <button
      type="button"
      onClick={onClick}
      className="pressable"
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '11px 14px 12px',
        border: 'none',
        borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
        background: active ? 'var(--accent-soft)' : 'transparent',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            color: active ? 'var(--accent-text)' : 'var(--text-body)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {s.build.client?.name ?? '—'}
        </span>
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', flexShrink: 0 }}>
          {s.done}/{total}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '2px 0 8px', minWidth: 0 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {s.build.name}
        </span>
        {s.build.auto_release && s.build.status === 'active' ? (
          <span title={cadenceLabel(s.build)} style={{ display: 'inline-flex', color: 'var(--status-up)', flexShrink: 0 }}>
            <Icon name="zap" size={12} />
          </span>
        ) : null}
        {s.queued > 0 ? (
          <span title={`${s.queued} on the Fulfillment board`} style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--status-warn)', flexShrink: 0 }} />
        ) : null}
      </div>
      <ProgressBar done={s.done} queued={s.queued} total={total} height={4} />
    </button>
  );
}

function SortableRow({ id, children }: { id: string; children: (handle: React.ReactNode) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const handle = (
    <button
      ref={setActivatorNodeRef}
      type="button"
      aria-label="Drag to reorder"
      {...attributes}
      {...listeners}
      style={{ display: 'inline-flex', padding: 2, border: 'none', background: 'transparent', color: 'var(--text-faint)', cursor: 'grab', touchAction: 'none' }}
    >
      <Icon name="grip-vertical" size={14} />
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 2 : undefined,
        background: isDragging ? 'var(--surface-card)' : undefined,
        boxShadow: isDragging ? 'var(--shadow-pop)' : undefined,
        borderRadius: isDragging ? 'var(--radius-md)' : undefined,
      }}
    >
      {children(handle)}
    </div>
  );
}

export default function BuildsView(props: BuildsViewProps) {
  const { summaries, filter, selected, pages } = props;
  const [query, setQuery] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newPath, setNewPath] = useState('');
  const [newKind, setNewKind] = useState<PageKind>('page');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ title: '', path: '', note: '' });
  const [showAllDone, setShowAllDone] = useState(false);

  const counts = useMemo(() => {
    const c: Record<BuildStatus, number> = { active: 0, paused: 0, finished: 0 };
    summaries.forEach((s) => { c[s.build.status] += 1; });
    return c;
  }, [summaries]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return summaries
      .filter((s) => s.build.status === filter)
      .filter((s) => !q || `${s.build.client?.name ?? ''} ${s.build.name}`.toLowerCase().includes(q))
      // Builds with pages left first, then the ones that are all shipped
      .sort((a, b) => Number(a.queued + a.backlog === 0) - Number(b.queued + b.backlog === 0)
        || (a.build.client?.name ?? '').localeCompare(b.build.client?.name ?? ''));
  }, [summaries, filter, query]);

  const done = pages.filter((p) => p.status === 'done');
  const queued = pages.filter((p) => p.status === 'queued');
  const backlog = pages.filter((p) => p.status === 'backlog');
  const total = pages.length;
  const pct = total ? Math.round((done.length / total) * 100) : 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const ids = backlog.map((p) => p.id);
    props.onReorderBacklog(arrayMove(ids, ids.indexOf(String(e.active.id)), ids.indexOf(String(e.over.id))));
  };

  const submitAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    props.onAddPage(newTitle.trim(), newPath.trim() || null, newKind);
    setNewTitle('');
    setNewPath('');
  };

  const idx = selected ? visible.findIndex((s) => s.build.id === selected.id) : -1;
  const step = (d: number) => {
    if (!visible.length) return;
    const i = idx < 0 ? 0 : (idx + d + visible.length) % visible.length;
    props.onSelect(visible[i].build.id);
  };

  const pageMenu = (p: BuildPage) => (
    <KebabMenu
      actions={[
        p.status !== 'done' && { label: 'Mark done', icon: faCheck, onClick: () => props.onSetStatus(p, 'done') },
        p.status !== 'backlog' && { label: 'Back to backlog', icon: faRotateLeft, onClick: () => props.onSetStatus(p, 'backlog') },
        { label: 'Edit', icon: faPen, onClick: () => { setEditingId(p.id); setDraft({ title: p.title, path: p.path ?? '', note: p.note ?? '' }); } },
        { label: 'Delete', icon: faTrash, danger: true, onClick: () => props.onDeletePage(p) },
      ]}
    />
  );

  const editor = (p: BuildPage) => (
    <div style={{ display: 'grid', gap: 8, padding: '10px 0' }}>
      <Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} placeholder="Title" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
        <Input value={draft.path} onChange={(e) => setDraft((d) => ({ ...d, path: e.target.value }))} placeholder="/path" />
        <Input value={draft.note} onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} placeholder="Note for whoever builds it" />
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Button size="sm" disabled={props.busy} onClick={() => {
          props.onSavePage(p, { title: draft.title.trim() || p.title, path: draft.path.trim() || null, note: draft.note.trim() || null });
          setEditingId(null);
        }}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
      </div>
    </div>
  );

  const meta = (p: BuildPage) => [p.path, p.note].filter(Boolean).join(' · ');

  const queueRow = (p: BuildPage, n: number | null, handle: React.ReactNode, isNext: boolean) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 6px 9px 4px', minHeight: 48 }}>
      <span style={{ width: 18, display: 'inline-flex', justifyContent: 'center' }}>{handle}</span>
      <span
        className="t-num"
        style={{
          width: 22, height: 22, borderRadius: 999, flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'var(--text-2xs)',
          background: n === null ? 'var(--status-warn-soft)' : isNext ? 'var(--accent-soft)' : 'var(--surface-raised)',
          color: n === null ? 'var(--status-warn)' : isNext ? 'var(--accent-text)' : 'var(--text-muted)',
        }}
      >
        {n === null ? <Icon name="clock" size={12} /> : n}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
          {p.kind === 'screen' ? <Badge tone="info">Screen</Badge> : null}
          {isNext ? <Badge tone="accent">Up next</Badge> : null}
        </div>
        {meta(p) ? (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{meta(p)}</div>
        ) : null}
      </div>
      {n === null ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)', flexShrink: 0 }}>
          On the board{p.task?.due_date ? ` · ${formatDate(p.task.due_date, 'MMM d')}` : ''}
        </span>
      ) : null}
      {pageMenu(p)}
    </div>
  );

  const doneShown = showAllDone ? done : done.slice(0, 8);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)]" style={{ gap: 'var(--space-5)', alignItems: 'start' }}>
      {/* ── Rail ─────────────────────────────────────────── */}
      <aside className="hidden lg:block" style={{ ...surface, position: 'sticky', top: 16, overflow: 'hidden' }}>
        <div style={{ padding: 12, display: 'grid', gap: 10, borderBottom: '1px solid var(--border-hairline)' }}>
          <SegmentedControl
            options={FILTERS.map((f) => ({ id: f.id, label: `${f.label} ${counts[f.id]}` }))}
            value={filter}
            onChange={(id) => props.onFilter(id as BuildStatus)}
            style={{ width: '100%' }}
          />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a client…" />
        </div>
        <div style={{ maxHeight: 'calc(100vh - 230px)', overflowY: 'auto' }}>
          {visible.length === 0 ? (
            <div style={{ padding: 20, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>No {filter} builds.</div>
          ) : (
            visible.map((s, i) => (
              <div key={s.build.id} style={{ borderTop: i ? '1px solid var(--border-hairline)' : 'none' }}>
                <RailRow s={s} active={selected?.id === s.build.id} onClick={() => props.onSelect(s.build.id)} />
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Phones / narrow: a picker instead of the rail */}
      <div className="lg:hidden">
       <div style={{ display: 'grid', gap: 10 }}>
        <SegmentedControl
          options={FILTERS.map((f) => ({ id: f.id, label: `${f.label} ${counts[f.id]}` }))}
          value={filter}
          onChange={(id) => props.onFilter(id as BuildStatus)}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <SearchableSelect
              placeholder="Pick a build…"
              searchPlaceholder="Search clients…"
              options={visible.map((s) => ({ value: s.build.id, label: `${s.build.client?.name ?? '—'} · ${s.build.name}` }))}
              value={selected?.id ?? null}
              onChange={(v) => v && props.onSelect(v)}
            />
          </div>
          <IconButton icon="chevron-left" variant="outline" label="Previous" onClick={() => step(-1)} />
          <IconButton icon="chevron-right" variant="outline" label="Next" onClick={() => step(1)} />
        </div>
       </div>
      </div>

      {/* ── Detail ───────────────────────────────────────── */}
      {selected ? (
        <section style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--space-5)', minWidth: 0 }}>
          {/* Hero */}
          <div style={{ ...surface, padding: 'var(--space-6, 24px)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={eyebrow}>{selected.name}</div>
                <h2 style={{ margin: '4px 0 0', fontSize: 'var(--text-2xl)', lineHeight: 1.15, color: 'var(--text-primary, var(--text-body))', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>
                  {selected.client?.name ?? '—'}
                </h2>
              </div>
              <div className="hidden lg:flex" style={{ gap: 4, alignItems: 'center' }}>
                <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginRight: 4 }}>
                  {idx + 1} of {visible.length}
                </span>
                <IconButton icon="chevron-left" size="sm" label="Previous build" onClick={() => step(-1)} />
                <IconButton icon="chevron-right" size="sm" label="Next build" onClick={() => step(1)} />
              </div>
              <Button size="sm" variant="secondary" onClick={props.onEdit}>Settings</Button>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '22px 0 10px', flexWrap: 'wrap' }}>
              <span className="t-num" style={{ fontSize: 'var(--text-3xl)', lineHeight: 1, color: 'var(--text-body)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>{pct}%</span>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                {done.length} of {total} {total === 1 ? 'page' : 'pages'} shipped
              </span>
            </div>
            <ProgressBar done={done.length} queued={queued.length} total={total} height={8} />
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 'var(--text-xs)', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
              {[
                { c: 'var(--accent)', l: `${done.length} shipped` },
                { c: 'var(--status-warn)', l: `${queued.length} on the board` },
                { c: 'var(--border-strong)', l: `${backlog.length} to build` },
              ].map((k) => (
                <span key={k.l} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: k.c }} />{k.l}
                </span>
              ))}
            </div>

            {/* Release strip */}
            <div
              style={{
                marginTop: 22, padding: '14px 16px', borderRadius: 'var(--radius-md)',
                background: 'var(--surface-raised)', border: '1px solid var(--border-hairline)',
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: selected.auto_release && selected.status === 'active' ? 'var(--status-up-soft)' : 'var(--surface-card)',
                  color: selected.auto_release && selected.status === 'active' ? 'var(--status-up)' : 'var(--text-faint)',
                }}
              >
                <Icon name="zap" size={16} />
              </span>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                  {selected.status !== 'active'
                    ? `Build is ${selected.status}`
                    : selected.auto_release
                      ? `${cadenceLabel(selected)}, one page to ${props.assigneeName ?? 'the board (unassigned)'}`
                      : 'Auto-release is off'}
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: 2 }}>
                  {selected.status !== 'active'
                    ? 'Nothing goes to the Fulfillment board.'
                    : !selected.auto_release
                      ? `Turn it on to send ${cadenceLabel(selected).toLowerCase()}, or release by hand.`
                      : backlog.length === 0
                        ? 'Backlog is empty.'
                        : `Next: ${props.nextRelease ? formatDate(`${props.nextRelease}T12:00:00`, 'EEE, MMM d') : '—'} · ${backlog[0].title}${selected.hold_while_open ? ' · waits for the open page' : ''}`}
                </div>
              </div>
              <Switch checked={selected.auto_release && selected.status === 'active'} onChange={props.onToggleAuto} />
              <Button size="sm" onClick={props.onReleaseNext} disabled={props.busy || backlog.length === 0}>
                Release next now
              </Button>
            </div>
            {props.notice || props.error ? (
              <div style={{ marginTop: 10, fontSize: 'var(--text-xs)', color: props.error ? 'var(--danger-400)' : 'var(--status-up)' }}>
                {props.error ?? props.notice}
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]" style={{ gap: 'var(--space-5)', alignItems: 'start' }}>
            {/* Queue */}
            <div style={{ ...surface, padding: '18px 18px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                <h3 style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--text-body)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>To build</h3>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Drag to set the order</span>
              </div>
              {props.pagesLoading ? (
                <div style={{ padding: '16px 0', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading…</div>
              ) : (
                <>
                  {queued.map((p, i) => (
                    <div key={p.id} style={{ borderTop: i ? '1px solid var(--border-hairline)' : 'none' }}>
                      {editingId === p.id ? editor(p) : queueRow(p, null, null, false)}
                    </div>
                  ))}
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={backlog.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                      {backlog.map((p, i) => (
                        <div key={p.id} style={{ borderTop: i || queued.length ? '1px solid var(--border-hairline)' : 'none' }}>
                          <SortableRow id={p.id}>
                            {(handle) => (editingId === p.id ? editor(p) : queueRow(p, i + 1, handle, i === 0))}
                          </SortableRow>
                        </div>
                      ))}
                    </SortableContext>
                  </DndContext>
                  {queued.length + backlog.length === 0 ? (
                    <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                      Everything is built. Add a page below, or mark the build finished.
                    </div>
                  ) : null}
                  <form
                    onSubmit={submitAdd}
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 10, paddingTop: 12, borderTop: '1px dashed var(--border-hairline)' }}
                  >
                    <span className="hidden sm:inline-flex" style={{ width: 18, justifyContent: 'center', color: 'var(--text-faint)' }}><Icon name="plus" size={14} /></span>
                    <div style={{ flex: '2 1 160px', minWidth: 0 }}>
                      <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Add a page…" />
                    </div>
                    <div className="hidden sm:block" style={{ flex: 1, minWidth: 0 }}>
                      <Input value={newPath} onChange={(e) => setNewPath(e.target.value)} placeholder="/path" />
                    </div>
                    <SegmentedControl
                      options={[{ id: 'page', label: 'Page' }, { id: 'screen', label: 'Screen' }]}
                      value={newKind}
                      onChange={(id) => setNewKind(id as PageKind)}
                    />
                    <Button type="submit" size="sm" variant="secondary" disabled={props.busy || !newTitle.trim()}>Add</Button>
                  </form>
                </>
              )}
            </div>

            {/* Shipped */}
            <div style={{ ...surface, padding: '18px 18px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                <h3 style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--text-body)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>Shipped</h3>
                <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{done.length}</span>
              </div>
              {done.length === 0 ? (
                <div style={{ padding: '16px 0', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>No first drafts yet.</div>
              ) : (
                <>
                  {doneShown.map((p, i) => (
                    <div key={p.id} style={{ borderTop: i ? '1px solid var(--border-hairline)' : 'none' }}>
                      {editingId === p.id ? editor(p) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', minHeight: 40 }}>
                          <span style={{ color: 'var(--status-up)', display: 'inline-flex', flexShrink: 0 }}><Icon name="check-circle-2" size={16} /></span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                            {p.path ? <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.path}</div> : null}
                          </div>
                          {p.task_id && p.done_at ? (
                            <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', flexShrink: 0 }}>{formatDate(p.done_at, 'MMM d')}</span>
                          ) : null}
                          {pageMenu(p)}
                        </div>
                      )}
                    </div>
                  ))}
                  {done.length > 8 ? (
                    <button
                      type="button"
                      onClick={() => setShowAllDone((v) => !v)}
                      style={{ width: '100%', padding: '10px 0 6px', border: 'none', borderTop: '1px solid var(--border-hairline)', background: 'transparent', color: 'var(--accent-text)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}
                    >
                      {showAllDone ? 'Show fewer' : `Show all ${done.length}`}
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </section>
      ) : (
        <div style={{ ...surface, padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
          No {filter} builds. Create one, add its pages, and pick how often the next page goes to the Fulfillment board.
        </div>
      )}
    </div>
  );
}
